import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useClient } from '../../contexts/ClientContext';
import { useAuth } from '../../contexts/AuthContext';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const ClientDashboard = () => {
    const { clients, loadingClients, addClient, updateClient, setActiveClient } = useClient();
    const { logout } = useAuth();
    const navigate = useNavigate();

    const [isAdding, setIsAdding] = useState(false);
    const [editingClientId, setEditingClientId] = useState(null);
    const [formData, setFormData] = useState({
        name: '',
        pan: '',
        tan: '',
        gstinRegular: '',
        gstinTds: ''
    });
    const [searchQuery, setSearchQuery] = useState('');

    const handleSelectClient = (client) => {
        setActiveClient(client);
        navigate('/return-selection');
    };

    const handleSaveClient = async (e) => {
        e.preventDefault();
        try {
            if (editingClientId) {
                await updateClient(editingClientId, formData);
                toast.success('Client updated successfully!');
            } else {
                await addClient(formData);
                toast.success('Client added successfully!');
            }
            setIsAdding(false);
            setEditingClientId(null);
            setFormData({ name: '', pan: '', tan: '', gstinRegular: '', gstinTds: '' });
        } catch (error) {
            console.error('Save client error:', error.response?.data || error);
            toast.error(error.response?.data?.error || error.message || 'Failed to save client');
        }
    };

    const handleEditClick = (e, client) => {
        e.stopPropagation();
        setEditingClientId(client._id);
        setFormData({
            name: client.name || '',
            pan: client.pan || '',
            tan: client.tan || '',
            gstinRegular: client.gstinRegular || '',
            gstinTds: client.gstinTds || ''
        });
        setIsAdding(true);
        // scroll to top smoothly
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    if (loadingClients) {
        return <div className="min-h-screen flex items-center justify-center bg-[#0a0a0a]">
            <div className="text-white text-xl">Loading clients...</div>
        </div>;
    }

    const filteredClients = clients.filter(client => {
        const query = searchQuery.toLowerCase();
        return (
            (client.name && client.name.toLowerCase().includes(query)) ||
            (client.tan && client.tan.toLowerCase().includes(query)) ||
            (client.gstinRegular && client.gstinRegular.toLowerCase().includes(query)) ||
            (client.gstinTds && client.gstinTds.toLowerCase().includes(query))
        );
    });

    return (
        <div className="min-h-screen bg-[#0a0a0a] text-white p-8">
            <ToastContainer />
            <div className="max-w-6xl mx-auto">
                <div className="flex justify-between items-center mb-8">
                    <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-400 to-indigo-500 bg-clip-text text-transparent">
                        Client Dashboard
                    </h1>
                    <div className="flex gap-4">
                        <button 
                            onClick={() => {
                                setIsAdding(!isAdding);
                                if (isAdding) {
                                    setEditingClientId(null);
                                    setFormData({ name: '', pan: '', tan: '', gstinRegular: '', gstinTds: '' });
                                }
                            }}
                            className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 rounded-lg transition-colors font-medium"
                        >
                            {isAdding ? 'Cancel' : '+ Add New Client'}
                        </button>
                        <button 
                            onClick={() => { logout(); navigate('/'); }}
                            className="px-4 py-2 bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors border border-gray-700"
                        >
                            Logout
                        </button>
                    </div>
                </div>

                {isAdding && (
                    <div className="bg-[#111111] border border-gray-800 rounded-xl p-6 mb-8 animate-fade-in-down">
                        <h2 className="text-xl font-semibold mb-4 text-gray-200">
                            {editingClientId ? 'Edit Client' : 'Create New Client'}
                        </h2>
                        <form onSubmit={handleSaveClient} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm text-gray-400 mb-1">Company / Client Name *</label>
                                <input 
                                    type="text" required
                                    value={formData.name}
                                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                                    className="w-full bg-[#1a1a1a] border border-gray-700 rounded p-2 text-white placeholder-gray-600 focus:border-indigo-500 focus:outline-none"
                                    placeholder="Acme Corp"
                                />
                            </div>
                            <div>
                                <label className="block text-sm text-gray-400 mb-1">PAN Number</label>
                                <input 
                                    type="text" 
                                    value={formData.pan}
                                    onChange={(e) => setFormData({...formData, pan: e.target.value.toUpperCase()})}
                                    className="w-full bg-[#1a1a1a] border border-gray-700 rounded p-2 text-white placeholder-gray-600 focus:border-indigo-500 focus:outline-none"
                                    placeholder="ABCDE1234F"
                                />
                            </div>
                            <div>
                                <label className="block text-sm text-gray-400 mb-1">TAN Number (For TDS)</label>
                                <input 
                                    type="text"
                                    value={formData.tan}
                                    onChange={(e) => setFormData({...formData, tan: e.target.value.toUpperCase()})}
                                    className="w-full bg-[#1a1a1a] border border-gray-700 rounded p-2 text-white placeholder-gray-600 focus:border-indigo-500 focus:outline-none"
                                    placeholder="DELA12345B"
                                />
                            </div>
                            <div>
                                <label className="block text-sm text-gray-400 mb-1">GSTIN (For GSTR-1/3B)</label>
                                <input 
                                    type="text"
                                    value={formData.gstinRegular}
                                    onChange={(e) => setFormData({...formData, gstinRegular: e.target.value.toUpperCase()})}
                                    className="w-full bg-[#1a1a1a] border border-gray-700 rounded p-2 text-white placeholder-gray-600 focus:border-indigo-500 focus:outline-none"
                                    placeholder="07AAAAA0000A1Z5"
                                />
                            </div>
                            <div>
                                <label className="block text-sm text-gray-400 mb-1">GSTIN (For TDS GSTR-7)</label>
                                <input 
                                    type="text"
                                    value={formData.gstinTds}
                                    onChange={(e) => setFormData({...formData, gstinTds: e.target.value.toUpperCase()})}
                                    className="w-full bg-[#1a1a1a] border border-gray-700 rounded p-2 text-white placeholder-gray-600 focus:border-indigo-500 focus:outline-none"
                                    placeholder="07AAAAA0000A1Z5"
                                />
                            </div>
                            <div className="md:col-span-2 flex justify-end mt-2">
                                <button type="submit" className="px-6 py-2 bg-indigo-600 hover:bg-indigo-700 rounded-lg transition-colors font-medium">
                                    {editingClientId ? 'Update Client' : 'Save Client'}
                                </button>
                            </div>
                        </form>
                    </div>
                )}

                <div className="mb-4 flex items-center bg-[#111111] border border-gray-800 rounded-lg px-4 py-2">
                    <svg className="w-5 h-5 text-gray-500 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                    <input 
                        type="text" 
                        placeholder="Search by Name, TAN, or GSTIN..." 
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="bg-transparent border-none text-white focus:ring-0 w-full focus:outline-none"
                    />
                </div>

                <div className="bg-[#111111] border border-gray-800 rounded-xl overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full text-left border-collapse">
                            <thead>
                                <tr className="bg-[#1a1a1a] border-b border-gray-800 text-sm text-gray-400">
                                    <th className="p-4 font-medium w-16 text-center">S. No.</th>
                                    <th className="p-4 font-medium">Company / Client Name</th>
                                    <th className="p-4 font-medium">PAN Number</th>
                                    <th className="p-4 font-medium">TAN Number</th>
                                    <th className="p-4 font-medium">GSTIN</th>
                                    <th className="p-4 font-medium">TDS GSTIN</th>
                                    <th className="p-4 font-medium text-center">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredClients.length === 0 ? (
                                    <tr>
                                        <td colSpan="7" className="p-8 text-center text-gray-500">
                                            {searchQuery ? 'No clients match your search.' : 'No clients found. Add your first client above.'}
                                        </td>
                                    </tr>
                                ) : (
                                    filteredClients.map((client, index) => (
                                        <tr 
                                            key={client._id}
                                            onClick={() => handleSelectClient(client)}
                                            className="border-b border-gray-800/50 hover:bg-[#1a1a1a] cursor-pointer transition-colors"
                                        >
                                            <td className="p-4 text-gray-400 text-center text-sm">
                                                {index + 1}
                                            </td>
                                            <td className="p-4 font-medium text-gray-200">
                                                {client.name}
                                            </td>
                                            <td className="p-4 text-gray-400 font-mono text-sm">
                                                {client.pan || '-'}
                                            </td>
                                            <td className="p-4 text-gray-400 font-mono text-sm">
                                                {client.tan || '-'}
                                            </td>
                                            <td className="p-4 text-gray-400 font-mono text-sm">
                                                {client.gstinRegular || '-'}
                                            </td>
                                            <td className="p-4 text-gray-400 font-mono text-sm">
                                                {client.gstinTds || '-'}
                                            </td>
                                            <td className="p-4 text-center">
                                                <button 
                                                    onClick={(e) => handleEditClick(e, client)}
                                                    className="px-3 py-1.5 bg-indigo-600/20 text-indigo-400 hover:bg-indigo-600 hover:text-white rounded transition-colors text-sm font-medium"
                                                >
                                                    Edit
                                                </button>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ClientDashboard;
